import { useEffect, useRef, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  Typography,
  TableContainer,
  TableCell,
  TableHead,
  TableRow,
  TableBody,
  Table,
  Paper,
  DialogActions,
  Checkbox,
  FormControlLabel,
  IconButton,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CloseIcon from "@mui/icons-material/Close";
import { useStores } from "../../../use-stores";
import { OrderServices } from "../../../../../models/Order/OrderProduct";
import { useNotification } from "../../../../../utils/NotificationsContext";
import AppTableHeaderCell from "../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTableRowCell from "../../../../../views/components/common/pages/AppTableRowCell";
import FormSmallTextField from "../../../../../views/components/common/form/FormTextField";
interface AddServicesProps {
  list?: OrderServices[];
  onSubmit: (services: OrderServices[]) => void;
  onClose: () => void;
  isLoading: boolean;
  orderId?: number;
  handleInvoiceQuantity?: boolean;
}

export default function AddServicePopup(props: AddServicesProps) {
  const { onClose } = props;
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedServices, setSelectedServices] = useState<OrderServices[]>([]);
  const [filteredServices, setFilteredServices] = useState<OrderServices[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { showError } = useNotification();
  const { serviceStore } = useStores();

  useEffect(() => {
    if (props.list && props.list.length > 0) {
      setFilteredServices(props.list);
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        handleDropdownClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelectService = (service: OrderServices) => {
    const index = selectedServices.findIndex(
      (p) => p.id === service.id && p.custom_name === service.custom_name
    );
    if (index === -1) {
      service.avail_invoice_qty = service.quantity - service.invoiced_quantity;
      setSelectedServices([...selectedServices, service]);
    } else {
      const updatedServices = [...selectedServices];
      updatedServices.splice(index, 1);
      setSelectedServices(updatedServices);
    }
  };

  const handleSearch = (query: string) => {
    setSearchTerm(query);
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    if (!props.list && query.length > 2) {
      debounceTimeoutRef.current = setTimeout(() => {
        serviceStore
          .getServices({ search_text: query })
          .then(() => {
            setFilteredServices(
              serviceStore.getOrderServicesFromServices(serviceStore.services)
            );
          })
          .catch(() => {
            setFilteredServices([]);
          });
        setIsDropdownOpen(true);
      }, 500);
    } else {
      if (props?.list?.length === 0) return;
      const filtered = props?.list?.filter((product: OrderServices) =>
        product.name!.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredServices(filtered || []);
      setIsDropdownOpen(true);
    }
  };

  const removeService = (service: OrderServices) => {
    const index = selectedServices.findIndex((p) => p.id === service.id);
    const updatedServices = [...selectedServices];
    updatedServices.splice(index, 1);
    setSelectedServices(updatedServices);
  };

  const handleClearAll = () => {
    setSelectedServices([]);
  };

  const updateConfirmedServices = () => {
    if (selectedServices.length === 0) {
      return;
    }

    for (let i = 0; i < selectedServices.length; i++) {
      if (
        !selectedServices[i].custom_name ||
        selectedServices[i].custom_name === ""
      ) {
        showError(
          "Error Occured",
          `Please add custom label for ${selectedServices[i].name} service`
        );
        return;
      }
    }

    const updated_services: OrderServices[] = selectedServices.map(
      (service) => {
        return {
          ...service,
          quantity: props.handleInvoiceQuantity
            ? service.avail_invoice_qty || 1
            : service.quantity || 1,
        };
      }
    );

    props.onSubmit(updated_services);
  };

  const updateServiceQuantity = (value: number, service: OrderServices) => {
    const index = selectedServices.findIndex(
      (selectedService) => selectedService.id === service.id
    );
    if (index !== -1) {
      if (props.handleInvoiceQuantity) {
        const updatedService = [...selectedServices];
        updatedService[index].avail_invoice_qty = value;
        setSelectedServices(updatedService);
      } else {
        const updatedService = [...selectedServices];
        updatedService[index].quantity = value;
        setSelectedServices(updatedService);
      }
    }
  };

  const updateServiceCustomName = (value: any, service: OrderServices) => {
    const index = selectedServices.findIndex(
      (selectedService) =>
        selectedService.id === service.id &&
        service.custom_name === selectedService.custom_name
    );
    if (index !== -1) {
      const updatedService = [...selectedServices];
      updatedService[index].custom_name = value;
      setSelectedServices(updatedService);
    }
  };

  const handleSearchBarClick = () => {
    if (filteredServices.length > 0) {
      setIsDropdownOpen(true);
    }
  };

  const handleDropdownClose = () => {
    setIsDropdownOpen(false);
    setSearchTerm("");
  };

  return (
    <Dialog
      open
      sx={{
        "& .MuiDialog-paper": {
          maxWidth: "53.125rem",
          width: "100%",
          height: "100%",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          paddingX: 5,
          paddingBottom: 1,
          paddingTop: 3.5,
        }}
      >
        <CustomTypography variant="heading4" fontWeight={700}>
          Add Services
        </CustomTypography>
        <IconButton
          sx={{
            color: "black",
            "&:hover": {
              backgroundColor: "grey",
            },
          }}
          size="small"
          onClick={() => {
            setSelectedServices([]);
            onClose();
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ paddingX: 5, paddingBottom: 0, paddingTop: 2.5 }}>
        <CustomTextField
          value={searchTerm}
          onSearch={(query) => handleSearch(query)}
          onClick={handleSearchBarClick}
          width={"100%"}
        />
      </Box>
      <DialogContent sx={{ paddingX: 5, paddingTop: 0 }}>
        {isDropdownOpen && (
          <Box
            ref={dropdownRef}
            sx={{
              paddingX: 0,
              paddingTop: 0,
              paddingBottom: 0,
              position: "relative",
            }}
          >
            <Box
              sx={{
                maxHeight: 400,
                overflowY: "auto",
                boxShadow: 1,
                backgroundColor: "white",
                borderRadius: 2,
                marginTop: 0,
                padding: 2,
                position: "absolute",
                zIndex: 2,
                width: "100%",
              }}
            >
              {filteredServices.map((service: any) => (
                <Box
                  key={service.id}
                  display="flex"
                  alignItems="center"
                  p={1}
                  borderRadius={1}
                  sx={{
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "#f0f0f0" },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectService(service);
                    setSearchTerm("");
                  }}
                >
                  <FormControlLabel
                    sx={{
                      marginRight: 0,
                    }}
                    control={
                      <Checkbox
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => handleSelectService(service)}
                        checked={selectedServices.some(
                          (selectedService) =>
                            selectedService.id === service.id &&
                            selectedService.custom_name === service.custom_name
                        )}
                        color="primary"
                      />
                    }
                    label={""}
                  />
                  <Box sx={{ marginRight: 1.5 }}>
                    <Typography fontWeight="bold">
                      {service.custom_name
                        ? service.name + ` (${service.custom_name})`
                        : service.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Price: {service.selling_price}
                    </Typography>
                  </Box>
                </Box>
              ))}
              {filteredServices.length === 0 && (
                <Typography sx={{ padding: 2 }} color="text.secondary">
                  No services found.
                </Typography>
              )}
            </Box>
          </Box>
        )}
        <Box>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            p={1}
          >
            <Box
              sx={{
                width: "100%",
                borderRadius: 2,
                maxHeight: "40vh",
                minHeight: "40vh",
              }}
            >
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ borderRadius: 2 }}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      <AppTableHeaderCell minWidth="3.125rem" fontWeight={700}>
                        ID
                      </AppTableHeaderCell>
                      <AppTableHeaderCell minWidth="3.125rem" fontWeight={700}>
                        SERVICE
                      </AppTableHeaderCell>
                      <AppTableHeaderCell minWidth="3.125rem" fontWeight={700}>
                        CUSTOM LABEL
                      </AppTableHeaderCell>
                      <AppTableHeaderCell minWidth="3.125rem" fontWeight={700}>
                        PRICE
                      </AppTableHeaderCell>
                      <AppTableHeaderCell minWidth="3.125rem" fontWeight={700}>
                        QUANTITY
                      </AppTableHeaderCell>
                      <AppTableHeaderCell minWidth="3.125rem" fontWeight={700}>
                        REMOVE
                      </AppTableHeaderCell>
                    </TableRow>
                  </TableHead>

                  {selectedServices.length === 0 ? (
                    <TableBody>
                      <TableRow>
                        <TableCell
                          colSpan={12}
                          sx={{
                            borderBottom: "none",
                            padding: 3,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              height: "3.125rem",
                              textAlign: "center",
                            }}
                          >
                            <CustomTypography
                              variant="body2"
                              fontWeight={700}
                              color="rgba(218, 218, 218, 1)"
                            >
                              NO SERVICES FOUND!
                            </CustomTypography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  ) : (
                    <TableBody>
                      {selectedServices?.map((service: OrderServices) => (
                        <TableRow key={service.id}>
                          <AppTableRowCell minWidth="3.125rem" fontWeight={700}>
                            {service.id}
                          </AppTableRowCell>
                          <AppTableRowCell minWidth="3.125rem" fontWeight={700}>
                            {service.name}
                          </AppTableRowCell>

                          <AppTableRowCell minWidth="3.125rem" fontWeight={700}>
                            <FormSmallTextField
                              variant="outlined"
                              color="secondary"
                              type="name"
                              fontSize="0.875rem"
                              value={service.custom_name}
                              sx={{
                                maxWidth: "11rem",
                                mb: "0",
                                "& .MuiInputBase-input": {
                                  textAlign: "center",
                                },
                              }}
                              disabled={props.orderId ? true : false}
                              onChange={(id, value) =>
                                updateServiceCustomName(value, service)
                              }
                            />
                          </AppTableRowCell>

                          <AppTableRowCell minWidth="3.125rem" fontWeight={700}>
                            {service.selling_price}
                          </AppTableRowCell>
                          <AppTableRowCell minWidth="3.125rem" fontWeight={700}>
                            <FormSmallTextField
                              variant="outlined"
                              color="secondary"
                              type="number"
                              fontSize="0.875rem"
                              value={
                                props.handleInvoiceQuantity
                                  ? service.avail_invoice_qty || ""
                                  : service.quantity || ""
                              }
                              sx={{
                                maxWidth: "5rem",
                                mb: "0",
                                "& input[type=number]": {
                                  textAlign: "center",
                                  "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button":
                                    {
                                      display: "none",
                                    },
                                  MozAppearance: "textfield",
                                },
                              }}
                              onChange={(id, value) =>
                                updateServiceQuantity(Number(value), service)
                              }
                            />
                          </AppTableRowCell>
                          <AppTableRowCell minWidth="3.125rem" fontWeight={700}>
                            <IconButton
                              onClick={() => {
                                removeService(service);
                              }}
                            >
                              <DeleteIcon
                                sx={{ color: "rgba(255, 0, 130, 0.8)" }}
                              />
                            </IconButton>
                          </AppTableRowCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ display: "flex", justifyContent: "center" }}>
        <Box sx={{ display: "flex", justifyContent: "center", gap: 3 }}>
          {props.isLoading ? (
            <AppButton
              label={
                <>
                  <CircularProgress size={16} color="inherit" /> ...Loading
                </>
              }
              variant="primaryBlock"
              customStyles={{
                padding: 3,
                paddingTop: 1,
                paddingBottom: 1,
                margin: 0,
                fontWeight: 500,
                fontSize: "1rem",
                marginBottom: "1rem",
              }}
              onClick={function (): void {}}
            />
          ) : (
            <AppButton
              label="Confirm Services"
              onClick={updateConfirmedServices}
              disabled={selectedServices.length === 0}
              variant="primaryBlock"
              customStyles={{
                padding: 3,
                paddingTop: 1,
                paddingBottom: 1,
                margin: 0,
                fontWeight: 500,
                fontSize: "1rem",
                marginBottom: "1rem",
              }}
            />
          )}

          <AppButton
            label="Clear All"
            onClick={handleClearAll}
            disabled={selectedServices.length === 0}
            variant="grayscale"
            customStyles={{
              padding: 7,
              paddingTop: 1,
              paddingBottom: 1,
              margin: 0,
              fontWeight: 600,
              fontSize: "1rem",
              marginBottom: "1rem",
            }}
          />
        </Box>
      </DialogActions>
    </Dialog>
  );
}
